import { useCallback, useEffect, useRef } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
    StringInput,
    StringInputBaseProps,
} from "@impero-com/common/components/form/fields/stringInput";
import { BaseRHFWidgetProps } from "@impero-com/common/components/form/rhfFields/types";
import { FormSection } from "@impero-com/common/components/formSection/formSection";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { ActionMenuProps, emitNotification, FormLabel } from "@impero-com/ui";
import {
    FieldValues,
    useController,
    useFieldArray,
    useForm,
    useWatch,
} from "react-hook-form";
import { useParams } from "react-router-dom";

import {
    UserIdAndRightForStandardAccessRight,
    EntityViewDto,
    UpdatableEntity,
} from "api/generated/model";
import { EditableUserTagListPlusInput } from "components/form/fields/editableUserTagListPlus";
import { RHFAccessRights } from "components/form/rhfFields/rhfAccessRights";
import { RHFEditableUserTagListInputProps } from "components/form/rhfFields/rhfEditableUserTagListPlus";
import { RHFMarkdownInput } from "components/form/rhfFields/rhfMarkdownInput";
import { RHFUserTypeaheadInput } from "components/form/rhfFields/rhfUserTypeaheadInput";
import { useEntityQuery } from "org/entities/queries/useEntityQuery";
import { useUpdateEntityMutation } from "org/entities/queries/useUpdateEntityMutation";
import {
    formatToAdminEditViewAccessRights,
    formatToUserIdAndRight,
} from "utils/accessRights";
import {
    ControlEntityAccessRightsInstance,
    RiskEntityAccessRightsInstance,
} from "utils/accessRights/models";
import { diffToPresence } from "utils/diff";
import { Guid } from "utils/guid";
import { useAvailableActions } from "utils/hooks/useAvailableActions";

import * as theme from "./ControlEntitiesForm.css";
import {
    EntityViewSideMenu,
    NAVIGATION_ID_ENTITY_DETAILS,
} from "./EntityViewSideMenu";
import {
    ControlEntityFormSchema,
    useControlEntityViewFormSchema,
} from "./useControlEntityViewFormSchema";
import { useDeleteAction } from "./useDeleteAction";

export function ControlEntitiesEditPage() {
    const { id } = useParams();
    const controlEntityId = Number(id);
    const { data } = useEntityQuery(controlEntityId);
    const canDelete = data.content.actions.delete;
    const { deleteAction, deleteDialogComponent } = useDeleteAction(
        data.content.actions.delete,
        controlEntityId
    );
    const actions: ActionMenuProps | undefined = canDelete
        ? {
              actions: [deleteAction],
              id: NAVIGATION_ID_ENTITY_DETAILS,
          }
        : undefined;

    return (
        <EntityViewSideMenu
            navigationId={NAVIGATION_ID_ENTITY_DETAILS}
            actions={actions}
        >
            {deleteDialogComponent}
            <View controlEntityId={controlEntityId} data={data} />
        </EntityViewSideMenu>
    );
}
interface ViewProps {
    controlEntityId: number;
    data: EntityViewDto;
}
function View({ controlEntityId, data }: ViewProps) {
    const { t } = useTranslation();
    const { userTag } = useAvailableActions();
    const canCreateTag = userTag.create;
    const { mutateAsync } = useUpdateEntityMutation();
    const schema = useControlEntityViewFormSchema();
    const { content, linkedResources } = data;
    const {
        actions: {
            edit: {
                controlAccessRights: canEditControlAccessRights,
                riskAccessRights: canEditRiskAccessRights,
                content: canEditContent,
            },
        },
        resource: controlEntity,
    } = content;
    const { users, controlAccessRights, riskAccessRights } = linkedResources;

    const { control } = useForm<ControlEntityFormSchema>({
        resolver: zodResolver(schema),
        mode: "onBlur",
        defaultValues: {
            name: controlEntity.name ?? "",
            description: controlEntity.description ?? "",
            responsibleId: controlEntity.responsibleId ?? undefined,
            kind: "Control",
            controlUserIdAndRights: formatToUserIdAndRight(controlAccessRights),
            riskUserIdAndRights: formatToUserIdAndRight(riskAccessRights),
            tagIds: controlEntity.tagIds ?? [],
        },
    });
    const initialRender = useRef(true);
    const initialControlAccessRights = useRef<string | null>(null);
    const watchedControlAccessRights = useWatch({
        control,
        name: "controlUserIdAndRights",
    });
    const { fields: controlAccessRightsFields } = useFieldArray({
        control,
        name: "controlUserIdAndRights",
    });
    const watchedControlAccessRightsColumn = controlAccessRightsFields.map(
        (_, idx) => watchedControlAccessRights[idx]?.right
    );

    const initialRiskAccessRights = useRef<string | null>(null);
    const watchedRiskAccessRights = useWatch({
        control,
        name: "riskUserIdAndRights",
    });
    const { fields: riskAccessRightsFields } = useFieldArray({
        control,
        name: "riskUserIdAndRights",
    });
    const watchedRiskAccessRightsColumn = riskAccessRightsFields.map(
        (_, idx) => watchedRiskAccessRights[idx]?.right
    );

    const nameController = useController({ name: "name", control });
    const descriptionController = useController({
        name: "description",
        control,
    });

    const validateAndMutateAccessRights = useCallback(
        async (
            userIdAndRights: UserIdAndRightForStandardAccessRight[],
            accessRightsType: "controlAccessRights" | "riskAccessRights"
        ) => {
            const {
                shape: { controlUserIdAndRights, riskUserIdAndRights },
            } = schema;

            let result;
            switch (accessRightsType) {
                case "controlAccessRights":
                    result = controlUserIdAndRights.safeParse(userIdAndRights);
                    break;
                case "riskAccessRights":
                    result = riskUserIdAndRights.safeParse(userIdAndRights);
                    break;
                default:
                    assertNever(accessRightsType);
            }
            if (result.success) {
                const transformedData =
                    formatToAdminEditViewAccessRights(userIdAndRights);
                await mutateAsync({
                    entityId: controlEntityId,
                    data: { [accessRightsType]: transformedData },
                });
                emitNotification("success", t("Entity_Update_Success"));
            } else {
                emitNotification("error", t("Entities_SubmitValidationError"));
            }
        },
        [schema, mutateAsync, t, controlEntityId]
    );

    const validateAndMutate = useCallback(
        async (payload: Partial<ControlEntityFormSchema>) => {
            const result = schema.partial().safeParse(payload);
            const initialTagIds = controlEntity.tagIds;
            if (result.success) {
                await mutateAsync({
                    entityId: controlEntityId,
                    data: toUpdatableEntity(result.data, initialTagIds),
                });
                emitNotification("success", t("Entity_Update_Success"));
            } else {
                emitNotification("error", t("Entities_SubmitValidationError"));
            }
        },
        [controlEntity.tagIds, mutateAsync, schema, t, controlEntityId]
    );

    // NOTE:
    // For name and description we patch only on blur, so we pass downt onBlur action.
    // For responsible and access rights we patch on every change, so we use watch in a useEffect.

    useEffect(() => {
        const currentControlAccessRightsValue = JSON.stringify(
            watchedControlAccessRights
        );
        const currentRiskAccessRightsValue = JSON.stringify(
            watchedRiskAccessRights
        );
        // On first render, store the initial value and skip patch
        if (initialRender.current) {
            initialRender.current = false;
            initialControlAccessRights.current =
                currentControlAccessRightsValue;
            initialRiskAccessRights.current = currentRiskAccessRightsValue;
            return;
        }
        // Only patch if the value actually changed from the last patched value
        if (
            currentControlAccessRightsValue !==
            initialControlAccessRights.current
        ) {
            validateAndMutateAccessRights(
                watchedControlAccessRights,
                "controlAccessRights"
            );
            initialControlAccessRights.current =
                currentControlAccessRightsValue;
        }

        if (currentRiskAccessRightsValue !== initialRiskAccessRights.current) {
            validateAndMutateAccessRights(
                watchedRiskAccessRights,
                "riskAccessRights"
            );
            initialRiskAccessRights.current = currentRiskAccessRightsValue;
        }
    }, [
        validateAndMutateAccessRights,
        watchedControlAccessRights,
        watchedControlAccessRightsColumn,
        watchedRiskAccessRights,
        watchedRiskAccessRightsColumn,
    ]);

    const initialNameValue = useRef(nameController.field.value);
    const initialDescriptionValue = useRef(descriptionController.field.value);
    return (
        <div>
            <FormSection id={"control-entity-details"}>
                <RHFStringInput
                    control={control}
                    name="name"
                    label={t("Entity_Label_Name")}
                    styleVariant="borderless"
                    disabled={!canEditContent}
                    onBlur={(newValue) => {
                        if (newValue !== initialNameValue.current) {
                            validateAndMutate({ name: newValue });
                            initialNameValue.current = newValue;
                        }
                    }}
                />
                <RHFMarkdownInput
                    control={control}
                    name="description"
                    label={t("Entity_Label_Description")}
                    disabled={!canEditContent}
                    styleVariant="borderless"
                    onBlur={(newValue) => {
                        if (newValue !== initialDescriptionValue.current) {
                            validateAndMutate({ description: newValue });
                            initialDescriptionValue.current = newValue;
                        }
                    }}
                />
                <RHFUserTypeaheadInput
                    control={control}
                    name="responsibleId"
                    label={t("Entity_Label_Responsible")}
                    disabled={!canEditContent}
                    onChange={(value) => {
                        validateAndMutate({ responsibleId: value });
                    }}
                />
                <div className={theme.legacyMaxWidthWrapper}>
                    <RHFEditableUserTagListInput
                        control={control}
                        name="tagIds"
                        canCreateTag={canCreateTag}
                        disabled={!canEditContent}
                        label={t("Entity_Label_Tags")}
                        onChange={(value: string[]) => {
                            validateAndMutate({ tagIds: value });
                        }}
                    />
                </div>
            </FormSection>

            <FormSection
                id={"control-entity-access-rights"}
                titleLabel={t("Entity_AccessRights_Title")}
            >
                <FormLabel>{t("Entity_Label_AccessRights")}</FormLabel>
                {watchedControlAccessRights && (
                    <RHFAccessRights
                        control={control}
                        users={users}
                        name="controlUserIdAndRights"
                        resourceAccessRights={ControlEntityAccessRightsInstance}
                        disabled={!canEditControlAccessRights}
                    />
                )}
            </FormSection>
            <FormSection id={"risk-entity-access-rights"}>
                <FormLabel>{"Risk access management"}</FormLabel>
                {watchedRiskAccessRights && (
                    <RHFAccessRights
                        control={control}
                        users={users}
                        name="riskUserIdAndRights"
                        resourceAccessRights={RiskEntityAccessRightsInstance}
                        disabled={!canEditRiskAccessRights}
                    />
                )}
            </FormSection>
        </div>
    );
}

const toUpdatableEntity = (
    data: Partial<ControlEntityFormSchema>,
    initialTagIds: string[]
): UpdatableEntity => {
    const { tagIds, ...rest } = data;
    return {
        ...rest,
        ...{
            tagIds: diffToPresence(new Set(initialTagIds), new Set(tagIds)),
        },
    };
};

function RHFEditableUserTagListInput<TFieldsValues extends FieldValues>({
    name,
    control,
    disabled,
    label,
    canCreateTag,
    onChange,
}: Omit<RHFEditableUserTagListInputProps<TFieldsValues>, "onChange"> & {
    onChange: (tagIds: string[]) => void;
}) {
    const { field, fieldState } = useController({ name, control, disabled });

    const handleSelect = (tagIds: Guid[]) => {
        field.onChange(tagIds);
        onChange(tagIds);
    };

    return (
        <EditableUserTagListPlusInput
            inputRef={field.ref}
            setSelectedTagIds={handleSelect}
            cascaderClassName={theme.tagCascader}
            canCreateTag={canCreateTag}
            disabled={field.disabled}
            error={fieldState.error?.message}
            label={label}
            selectedTagIds={field.value}
        />
    );
}

interface RHFStringInputProps<TFieldsValues extends FieldValues>
    extends BaseRHFWidgetProps<TFieldsValues, string>, StringInputBaseProps {
    onBlur?: (value: string) => void;
}

function RHFStringInput<T extends FieldValues>({
    control,
    name,
    label,
    disabled,
    styleVariant,
    onBlur,
}: RHFStringInputProps<T>) {
    const { field, fieldState } = useController({ name, control });
    // We only want to show the error message if the field has been touched and is dirty, meaning the user interacted with it
    const error = fieldState.isDirty ? fieldState.error?.message : undefined;
    const trimmedValue: string = field.value.trim();
    return (
        <StringInput
            label={label}
            value={field.value}
            error={error}
            onChange={field.onChange}
            onBlur={() => {
                onBlur?.(trimmedValue);
                field.onChange(trimmedValue);
                field.onBlur();
            }}
            styleVariant={styleVariant}
            disabled={disabled}
        />
    );
}
